import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/** What a finding means for the business — the only colour on the row. */
export type InsightTone = 'good' | 'bad' | 'watch' | 'neutral';

export interface Insight {
  id: string;
  tone: InsightTone;
  /** One sentence a vendor can act on: "Page two holds 869 searches you almost win". */
  title: ReactNode;
  /** The number behind it, or what to do. */
  detail?: ReactNode;
  action?: { label: ReactNode; href?: string; onClick?: () => void };
}

const DOT: Record<InsightTone, string> = {
  good: 'bg-success',
  bad: 'bg-destructive',
  watch: 'bg-warning',
  neutral: 'bg-muted-foreground/50',
};

/**
 * The reading of a page's numbers, in sentences — what changed, what is
 * working, what to do next. It sits above the charts, so a vendor who reads
 * nothing else still leaves with the point.
 */
export function InsightList({
  title,
  description,
  insights,
  className,
  testId,
}: {
  title: ReactNode;
  description?: ReactNode;
  insights: Insight[];
  className?: string;
  testId?: string;
}) {
  if (!insights.length) return null;
  return (
    <Card className={cn('gap-3', className)} data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="px-0">
        <ul className="divide-y">
          {insights.map((i) => (
            // On a phone the action drops under the sentence instead of squeezing it.
            <li key={i.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-3 sm:flex-nowrap sm:px-6" data-tone={i.tone}>
              <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', DOT[i.tone])} aria-hidden />
              <div className="min-w-0 flex-1 basis-[calc(100%-1.25rem)] sm:basis-auto">
                <p className="text-sm leading-snug">{i.title}</p>
                {i.detail && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{i.detail}</p>}
              </div>
              {i.action && <InsightAction action={i.action} />}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function InsightAction({ action }: { action: NonNullable<Insight['action']> }) {
  const cls =
    'ms-3 inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium sm:ms-0 sm:self-center text-foreground hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';
  const body = (
    <>
      {action.label}
      <ArrowRight className="size-3.5 rtl:rotate-180" />
    </>
  );
  if (action.href) {
    return (
      <Link to={action.href} className={cls}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={cls}>
      {body}
    </button>
  );
}
